/**
 * CommissionGridsPage — Configuration des grilles paliers (P2 batch 3).
 * Admin agence uniquement. Une grille contient N paliers (min_ca, max_ca?,
 * agent_pct, agency_pct, bonus_flat).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, ArrowLeft, Loader2, Layers } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ReseauLayout from '../../components/reseau/ReseauLayout';

function GridEditor({ initial, onSubmit, onCancel, saving }) {
    const [form, setForm] = useState(initial || {
        name: '', description: '', is_active: true,
        tiers: [{ min_ca: 0, max_ca: null, agent_pct: 40, agency_pct: 60, bonus_flat: 0 }],
    });

    const setTier = (i, field, value) => {
        const tiers = [...form.tiers];
        tiers[i] = { ...tiers[i], [field]: value === '' ? null : Number(value) };
        setForm({ ...form, tiers });
    };
    const addTier = () => {
        const last = form.tiers[form.tiers.length - 1] || { max_ca: 0 };
        setForm({
            ...form,
            tiers: [...form.tiers, {
                min_ca: (last.max_ca || 0) + 1, max_ca: null,
                agent_pct: 45, agency_pct: 55, bonus_flat: 0,
            }],
        });
    };
    const removeTier = (i) => {
        if (form.tiers.length === 1) return;
        setForm({ ...form, tiers: form.tiers.filter((_, k) => k !== i) });
    };

    const submit = (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error('Nom requis'); return; }
        onSubmit(form);
    };

    return (
        <form onSubmit={submit} className="p-5 rounded-xl space-y-4"
              data-testid="grid-editor"
              style={{ background: 'var(--card)', border: '1px solid rgba(0,0,0,0.06)' }}>
            <div className="grid sm:grid-cols-2 gap-2">
                <div>
                    <label className="text-xs font-medium block mb-1">Nom</label>
                    <input value={form.name}
                           onChange={(e) => setForm({ ...form, name: e.target.value })}
                           data-testid="grid-input-name"
                           className="w-full px-3 py-2 rounded-lg border text-sm"
                           style={{ borderColor: 'var(--border)' }} required />
                </div>
                <div>
                    <label className="text-xs font-medium block mb-1">Description</label>
                    <input value={form.description || ''}
                           onChange={(e) => setForm({ ...form, description: e.target.value })}
                           data-testid="grid-input-description"
                           className="w-full px-3 py-2 rounded-lg border text-sm"
                           style={{ borderColor: 'var(--border)' }} />
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold">Paliers</label>
                    <button type="button" onClick={addTier}
                            data-testid="grid-add-tier"
                            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded"
                            style={{ background: 'rgba(104,23,147,0.10)', color: '#681793' }}>
                        <Plus className="w-3 h-3" /> Ajouter un palier
                    </button>
                </div>
                <div className="space-y-2">
                    {form.tiers.map((t, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 rounded-lg"
                             data-testid={`grid-tier-${i}`}
                             style={{ background: 'var(--muted)' }}>
                            <div className="col-span-2">
                                <label className="text-[10px] uppercase block">CA min</label>
                                <input type="number" value={t.min_ca ?? 0}
                                       onChange={(e) => setTier(i, 'min_ca', e.target.value)}
                                       data-testid={`grid-tier-min-${i}`}
                                       className="w-full px-2 py-1 rounded border text-sm"
                                       style={{ borderColor: 'var(--border)' }} />
                            </div>
                            <div className="col-span-2">
                                <label className="text-[10px] uppercase block">CA max</label>
                                <input type="number" value={t.max_ca ?? ''}
                                       onChange={(e) => setTier(i, 'max_ca', e.target.value)}
                                       placeholder="∞"
                                       data-testid={`grid-tier-max-${i}`}
                                       className="w-full px-2 py-1 rounded border text-sm"
                                       style={{ borderColor: 'var(--border)' }} />
                            </div>
                            <div className="col-span-2">
                                <label className="text-[10px] uppercase block">% agent</label>
                                <input type="number" step="0.5" value={t.agent_pct}
                                       onChange={(e) => setTier(i, 'agent_pct', e.target.value)}
                                       data-testid={`grid-tier-agent-${i}`}
                                       className="w-full px-2 py-1 rounded border text-sm"
                                       style={{ borderColor: 'var(--border)' }} />
                            </div>
                            <div className="col-span-2">
                                <label className="text-[10px] uppercase block">% agence</label>
                                <input type="number" step="0.5" value={t.agency_pct}
                                       onChange={(e) => setTier(i, 'agency_pct', e.target.value)}
                                       data-testid={`grid-tier-agency-${i}`}
                                       className="w-full px-2 py-1 rounded border text-sm"
                                       style={{ borderColor: 'var(--border)' }} />
                            </div>
                            <div className="col-span-3">
                                <label className="text-[10px] uppercase block">Bonus flat (€)</label>
                                <input type="number" value={t.bonus_flat || 0}
                                       onChange={(e) => setTier(i, 'bonus_flat', e.target.value)}
                                       data-testid={`grid-tier-bonus-${i}`}
                                       className="w-full px-2 py-1 rounded border text-sm"
                                       style={{ borderColor: 'var(--border)' }} />
                            </div>
                            <div className="col-span-1 flex items-end justify-end">
                                {form.tiers.length > 1 && (
                                    <button type="button" onClick={() => removeTier(i)}
                                            data-testid={`grid-tier-remove-${i}`}
                                            className="p-1 rounded" style={{ color: '#dc2626' }}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active}
                       onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                       data-testid="grid-input-active" />
                Grille active
            </label>

            <div className="flex justify-end gap-2">
                <button type="button" onClick={onCancel}
                        className="text-sm px-3 py-2 rounded-lg"
                        style={{ color: 'var(--muted-foreground)' }}>Annuler</button>
                <button type="submit" disabled={saving}
                        data-testid="grid-submit"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold"
                        style={{ background: '#681793', color: '#fff' }}>
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    Enregistrer
                </button>
            </div>
        </form>
    );
}

export default function CommissionGridsPage() {
    const [grids, setGrids] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | 'new' | grid_id
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/commissions/grids');
            setGrids(r.data.items || []);
        } catch (e) {
            if (e?.response?.status === 403) {
                toast.error('Accès réservé aux administrateurs d\'agence');
                navigate('/agence/commissions');
            } else toast.error('Chargement impossible');
        } finally { setLoading(false); }
    }, [navigate]);

    useEffect(() => { load(); }, [load]);

    const save = async (form) => {
        setSaving(true);
        try {
            if (editing === 'new') {
                await api.post('/commissions/grids', form);
                toast.success('Grille créée');
            } else {
                await api.patch(`/commissions/grids/${editing.id}`, form);
                toast.success('Grille mise à jour');
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Enregistrement impossible');
        } finally { setSaving(false); }
    };

    const archive = async (grid) => {
        if (!window.confirm(`Archiver la grille "${grid.name}" ?`)) return;
        try {
            await api.delete(`/commissions/grids/${grid.id}`);
            toast.success('Grille archivée');
            load();
        } catch (e) { toast.error('Impossible'); }
    };

    return (
        <ReseauLayout headerTitle="Grilles de commissions"
                      headerSubtitle="Configurez les paliers appliqués aux mandats.">
            <main className="mx-auto px-4 sm:px-6 py-6 space-y-4"
                  data-testid="commission-grids-page"
                  style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                <div className="flex items-center justify-between gap-2">
                    <Link to="/agence/commissions"
                          className="inline-flex items-center gap-1 text-xs"
                          style={{ color: 'var(--muted-foreground)' }}>
                        <ArrowLeft className="w-3.5 h-3.5" /> Retour aux commissions
                    </Link>
                    {!editing && (
                        <button type="button" onClick={() => setEditing('new')}
                                data-testid="grid-create-btn"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold"
                                style={{ background: '#681793', color: '#fff' }}>
                            <Plus className="w-4 h-4" /> Nouvelle grille
                        </button>
                    )}
                </div>

                {editing && (
                    <GridEditor
                        initial={editing === 'new' ? null : editing}
                        onSubmit={save}
                        onCancel={() => setEditing(null)}
                        saving={saving}
                    />
                )}

                {loading ? (
                    <div className="text-center py-10">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto"
                                  style={{ color: '#681793' }} />
                    </div>
                ) : (
                    <ul className="space-y-2" data-testid="grid-list">
                        {grids.map((g) => (
                            <li key={g.id}
                                data-testid={`grid-row-${g.id}`}
                                className="p-4 rounded-xl border"
                                style={{ background: 'var(--card)', borderColor: 'var(--border)',
                                          opacity: g.is_active ? 1 : 0.6 }}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <Layers className="w-4 h-4" style={{ color: '#681793' }} />
                                            <p className="text-sm font-bold">{g.name}</p>
                                            {!g.is_active && (
                                                <span className="text-[10px] px-2 py-0.5 rounded-full"
                                                      style={{ background: 'rgba(75,85,99,0.12)', color: '#4b5563' }}>
                                                    Inactive
                                                </span>
                                            )}
                                        </div>
                                        {g.description && (
                                            <p className="text-xs mt-1"
                                               style={{ color: 'var(--muted-foreground)' }}>
                                                {g.description}
                                            </p>
                                        )}
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {(g.tiers || []).map((t, i) => (
                                                <span key={i}
                                                      className="text-[11px] px-2 py-1 rounded"
                                                      style={{ background: 'rgba(104,23,147,0.08)', color: '#681793' }}>
                                                    {t.min_ca || 0}
                                                    {t.max_ca != null ? `–${t.max_ca}` : '+'} €
                                                    &nbsp;→&nbsp;
                                                    {t.agent_pct}%/{t.agency_pct}%
                                                    {t.bonus_flat > 0 && ` +${t.bonus_flat}€`}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button type="button" onClick={() => setEditing(g)}
                                                data-testid={`grid-edit-${g.id}`}
                                                className="text-xs px-3 py-1 rounded"
                                                style={{ background: 'rgba(104,23,147,0.08)', color: '#681793' }}>
                                            Modifier
                                        </button>
                                        {g.is_active && (
                                            <button type="button" onClick={() => archive(g)}
                                                    data-testid={`grid-archive-${g.id}`}
                                                    className="p-1 rounded" style={{ color: '#dc2626' }}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </li>
                        ))}
                        {grids.length === 0 && !editing && (
                            <li className="p-6 rounded-xl text-center"
                                data-testid="grid-empty"
                                style={{ background: 'var(--card)', border: '1px solid rgba(0,0,0,0.06)' }}>
                                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                                    Aucune grille configurée. Créez-en une pour appliquer automatiquement
                                    les pourcentages selon le CA HT réalisé.
                                </p>
                            </li>
                        )}
                    </ul>
                )}
            </main>
        </ReseauLayout>
    );
}
